import React, { useState, useEffect, useRef } from "react";
import { axiosInstance } from "@hopper-b2b/api";
import { ButtonWrap } from "@hopper-b2b/ui";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import AxiosMockAdapter from "axios-mock-adapter";
import { endpoints, initResponses, formComponentMap } from "./MockSettings";
import type {
  Response,
  EndpointKey,
  EndpointResponseMap,
  Responses,
  FormComponent,
} from "./types";

export function MockCheckoutError() {
  const { matchesMobile } = useDeviceTypes();
  const mockRef = useRef<AxiosMockAdapter>();
  const [debugOpen, setDebugOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [rules, setRules] = useState<Responses>(initResponses);

  const isProd = import.meta.env.MODE === "production";

  // listen for `:debug` keyboard shortcut (DEV only)
  const seq = useRef<string[]>([]);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const onKey = (e: KeyboardEvent) => {
      seq.current.push(e.key);
      if (seq.current.slice(-6).join("") === ":debug") {
        setDebugOpen((o) => !o);
        seq.current = [];
      }
      if (seq.current.length > 10) seq.current.shift();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Listen for custom event to toggle from debug menu (non-prod)
  useEffect(() => {
    if (isProd) return;
    const handleToggle = () => setDebugOpen((o) => !o);
    window.addEventListener("toggle-mock-checkout-error", handleToggle);
    return () =>
      window.removeEventListener("toggle-mock-checkout-error", handleToggle);
  }, [isProd]);

  useEffect(() => {
    if (debugOpen) {
      const mock = new AxiosMockAdapter(axiosInstance, {
        onNoMatch: "passthrough",
      });
      mockRef.current = mock;

      endpoints.forEach((key) => {
        const rule = rules[key];
        if (!isProd && rule?.enabled) {
          mock.onAny(key).reply(() => {
            return [rule.errorCode, rule.response];
          });
        } else {
          mock.onAny(key).passThrough();
        }
      });
    } else {
      mockRef.current?.restore();
      mockRef.current = undefined;
    }
    return () => {
      mockRef.current?.restore();
      mockRef.current = undefined;
    };
  }, [rules, mockRef, debugOpen, isProd]);

  // draggable setup
  const [pos, setPos] = useState({ x: 20, y: 20 });
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current) {
      setPos({
        x: e.clientX - drag.current.dx,
        y: e.clientY - drag.current.dy,
      });
    }
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  if (isProd) return null;

  return (
    <>
      {debugOpen && (
        <div
          role="none"
          onMouseDown={!matchesMobile ? onPointerDown : undefined}
          onPointerDown={matchesMobile ? onPointerDown : undefined}
          onPointerUp={onPointerUp}
          onPointerMove={onPointerMove}
          style={{
            touchAction: "none",
            position: "fixed",
            top: pos.y,
            left: pos.x,
            width: 340,
            maxHeight: "80vh",
            overflowY: "auto",
            overflowX: "clip",
            background: "#fff",
            border: "1px solid #888",
            padding: 12,
            zIndex: 9999,
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          }}
        >
          <ButtonWrap
            onClick={() => setCollapsed(!collapsed)}
            style={{ width: "100%" }}
          >
            <p style={{ fontWeight: "bold", fontSize: 20 }}>
              {"Mock Errors"} {collapsed ? "(collapsed)" : ""}
            </p>
          </ButtonWrap>

          {!collapsed &&
            endpoints.map((key) => {
              const rule = rules[key];
              const FormComponent = formComponentMap[key];
              return (
                <div
                  key={key}
                  style={{
                    marginTop: 16,
                    padding: 8,
                    borderTop: "1px solid #ddd",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 4,
                      cursor: "pointer",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      style={{ cursor: "pointer", width: 20, height: 20 }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        setRules({
                          ...rules,
                          [key]: { ...rule, enabled: e.target.checked },
                        });
                      }}
                    />
                    <p
                      style={{
                        fontWeight: "normal",
                        fontSize: 16,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {key}
                    </p>
                  </label>

                  {FormComponent && (
                    <RuleForm
                      key={key}
                      endpoint={key}
                      rule={rule as Responses[typeof key]}
                      setRule={(newRule) =>
                        setRules((rs) => ({ ...rs, [key]: newRule }))
                      }
                      Form={FormComponent}
                    />
                  )}
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}

function RuleForm<K extends EndpointKey>(props: {
  endpoint: K;
  rule: Responses[K];
  setRule: (r: Response<EndpointResponseMap[K]>) => void;
  Form: FormComponent<K>;
}) {
  const { rule, setRule, Form } = props;
  if (!rule.enabled) return null;

  return (
    <Form
      errorObj={rule.response}
      onChange={(newResponse) => setRule({ ...rule, response: newResponse })}
    />
  );
}
